import React, { useEffect, useState } from "react"
import { useMutation } from "@apollo/client/react"
import { toast } from "sonner"
import {
  IconUserPlus,
  IconUserCheck,
  IconUserMinus,
  IconHeartFilled,
} from "@tabler/icons-react"
import Button from "@love-irish/core-button"
import { useSession } from "@love-irish/application-session-provider"
import { usePageProps } from "@love-irish/application-page-context"
import { UpsellModal } from "@love-irish/upsell-modal"
import type {
  FollowProfileMutationPayload,
  UnfollowProfileMutationPayload,
} from "@love-irish/types"
import FollowProfileMutation from "./follow_profile.graphql"
import UnfollowProfileMutation from "./unfollow_profile.graphql"

type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl"

export interface FollowButtonProps {
  /** Whether the viewer is currently following the profile. */
  isFollowing: boolean
  /** Shows a spinner and disables interaction while a mutation is in flight. */
  loading?: boolean
  disabled?: boolean
  onClick?: () => void
  size?: ButtonSize
  className?: string
}

/**
 * Presentational follow button. "Follow" (solid) when not following, "Following" (outline) when
 * following, swapping to a red "Unfollow" affordance on hover. Pure UI — pass `isFollowing` and an
 * `onClick`; the connected component owns the mutation and optimistic state.
 */
export const FollowButton: React.FC<FollowButtonProps> = ({
  isFollowing,
  loading = false,
  disabled = false,
  onClick,
  size = "sm",
  className,
}) => {
  if (isFollowing) {
    return (
      <Button
        type="button"
        color="secondary"
        variant="outline"
        size={size}
        loading={loading}
        disabled={disabled}
        onClick={onClick}
        className={`group ${className ?? ""}`}
        aria-pressed={true}
        data-testid="follow-button"
      >
        <span className="flex items-center gap-1.5 group-hover:hidden">
          <IconUserCheck size={16} />
          Following
        </span>
        <span className="hidden items-center gap-1.5 text-error group-hover:flex">
          <IconUserMinus size={16} />
          Unfollow
        </span>
      </Button>
    )
  }

  return (
    <Button
      type="button"
      color="primary"
      variant="solid"
      size={size}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-pressed={false}
      data-testid="follow-button"
    >
      <span className="flex items-center gap-1.5">
        <IconHeartFilled size={16} className="text-red-400 inline-block mr-2" />
        Follow
      </span>
    </Button>
  )
}

export interface ConnectedFollowButtonProps {
  /**
   * GraphQL global id (gid) of the profile to follow. Pass this when you have the profile from a
   * GraphQL query (e.g. the user popover).
   */
  profileId?: string
  /**
   * Server-issued signed GlobalID of the profile. Pass this on server-rendered pages; if omitted, the
   * button falls back to `usePageProps().resourceSgid` (set by `set_resource_for_tracking`).
   */
  profileSgid?: string
  /** Whether the viewer currently follows this profile (from the profile's `isFollowing` field). */
  isFollowing?: boolean
  /** True when the viewer owns this profile — the button hides (you can't follow yourself). */
  canEdit?: boolean
  size?: ButtonSize
  className?: string
}

type FollowData = { followProfile: FollowProfileMutationPayload }
type UnfollowData = { unfollowProfile: UnfollowProfileMutationPayload }

/**
 * Connected follow/unfollow button.
 *
 * - Hides when `canEdit` (your own profile).
 * - Logged out: opens the create-account upsell modal instead of mutating.
 * - Logged in: optimistically toggles, calls followProfile/unfollowProfile, and reverts on failure.
 *   The mutation returns the profile's updated `isFollowing` + `profileFollowsCount`, so Apollo
 *   refreshes every surface showing that profile (card counts, popover) by normalized id.
 *
 * ```tsx
 * <ConnectedFollowButton profileId={profile.id} isFollowing={profile.isFollowing} canEdit={profile.canEdit} />
 * ```
 */
export const ConnectedFollowButton: React.FC<ConnectedFollowButtonProps> = ({
  profileId,
  profileSgid,
  isFollowing = false,
  canEdit = false,
  size = "sm",
  className,
}) => {
  const { loggedIn } = useSession()
  const page = usePageProps()
  const [following, setFollowing] = useState(isFollowing)
  const [upsellOpen, setUpsellOpen] = useState(false)

  // Identify the profile by gid (GraphQL clients) or signed gid (page props). The mutation accepts
  // either; a raw id is never sent.
  const sgid = profileSgid ?? page.resourceSgid
  const variables = profileId
    ? { input: { profileId } }
    : sgid
    ? { input: { profileSgid: sgid } }
    : null

  // Keep local state in sync when the source-of-truth prop changes (e.g. Apollo cache update).
  useEffect(() => {
    setFollowing(isFollowing)
  }, [isFollowing])

  const [followProfile, { loading: followLoading }] = useMutation<FollowData>(
    FollowProfileMutation
  )
  const [unfollowProfile, { loading: unfollowLoading }] =
    useMutation<UnfollowData>(UnfollowProfileMutation)

  // Never render a follow button for your own profile, or when we can't identify the target.
  if (canEdit || !variables) return null

  const loading = followLoading || unfollowLoading

  const handleClick = async () => {
    if (!loggedIn) {
      setUpsellOpen(true)
      return
    }

    const next = !following
    setFollowing(next) // optimistic

    try {
      if (next) {
        const { data } = await followProfile({ variables })
        const payload = data?.followProfile
        if (!payload?.success) {
          setFollowing(false)
          toast.error(
            payload?.errors?.[0]?.message ?? "Could not follow profile."
          )
        }
      } else {
        const { data } = await unfollowProfile({ variables })
        const payload = data?.unfollowProfile
        if (!payload?.success) {
          setFollowing(true)
          toast.error(
            payload?.errors?.[0]?.message ?? "Could not unfollow profile."
          )
        }
      }
    } catch {
      setFollowing(!next) // revert on network error
      toast.error("Something went wrong. Please try again.")
    }
  }

  return (
    <>
      <FollowButton
        isFollowing={following}
        loading={loading}
        onClick={handleClick}
        size={size}
        className={className}
      />
      <UpsellModal
        isOpen={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        featureName="follow_profiles"
        icon={<IconUserPlus size={28} />}
        title="Follow profiles"
        description="Create a free account to follow people and keep up with what they share."
        primaryCta={{ label: "Create account", href: "/signup" }}
        secondaryCta={{
          label: "Maybe later",
          onClick: () => setUpsellOpen(false),
        }}
      />
    </>
  )
}

export default ConnectedFollowButton
